import { useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ErrorAlert from "../alerts/ErrorAlert";
import Iconify from "../iconify/Iconify";
import TrainerAssignmentDeleteDialog from "./TrainerAssignmentDeleteDialog";
import { formatDurationLabel } from "../../pages/trainer/trainerPageData";
import {
  DAY_ORDER,
  DAY_SHORT_LABELS,
  formatDateLabel,
  getSiteAccent,
} from "../../pages/trainer/trainerUtils";

export default function TrainerSummaryView({
  summary,
  error,
  deletingEntryKey,
  onNavigate,
  onToday,
  onDeleteEntry,
  onShowPlan,
  showDeleteActions = Boolean(onDeleteEntry),
}) {
  const theme = useTheme();
  const showDeleteText = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Stack spacing={2}>
      {error && <ErrorAlert errorMessage={error} />}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: alpha(theme.palette.primary.main, 0.25),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: { xs: 1.5, md: 2 },
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.08,
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
          }}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <IconButton
                  size="small"
                  onClick={() => onNavigate(-1)}
                  aria-label="Vorheriger Monat"
                >
                  <Iconify icon="mdi:chevron-left" width={22} />
                </IconButton>
                <Button
                  size="small"
                  variant="text"
                  onClick={onToday}
                  sx={{
                    minWidth: 0,
                    px: 1,
                    fontWeight: 700,
                    color: "text.primary",
                    textTransform: "none",
                  }}
                >
                  {summary.monthLabel}
                </Button>
                <IconButton
                  size="small"
                  onClick={() => onNavigate(1)}
                  aria-label="Nächster Monat"
                >
                  <Iconify icon="mdi:chevron-right" width={22} />
                </IconButton>
              </Stack>
              <Iconify
                icon="mdi:timer-check-outline"
                width={36}
                sx={{ color: alpha(theme.palette.primary.main, 0.3) }}
              />
            </Stack>

            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "primary.main",
                  lineHeight: 1.2,
                }}
              >
                {formatDurationLabel(summary.monthTotalMinutes)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.35 }}
              >
                {formatTrainingLabel(summary.monthTrainingCount)} im
                ausgewählten Monat.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        >
          <SummaryMetric
            label="Regelmäßig"
            value={formatTrainingLabel(summary.monthRegularCount)}
          />
          <SummaryMetric
            label="Einzeltermine"
            value={formatTrainingLabel(summary.monthSingleCount)}
          />
          <SummaryMetric
            label="Tage"
            value={`${summary.monthActivityDays.length}`}
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            pt: 1.5,
            pb: summary.regularCount > 0 ? 1 : 1.5,
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Iconify
              icon="mdi:repeat"
              width={18}
              sx={{ color: "primary.main" }}
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              Regelmäßig
            </Typography>
            <Chip
              size="small"
              label={`${summary.regularCount} aktiv`}
              sx={{
                height: 20,
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
              }}
            />
          </Stack>

          {summary.regularCount === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ pb: 0.5 }}>
              Keine regelmäßigen Trainings in diesem Monat.
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {DAY_ORDER.flatMap((day) => {
                const entries = [...(summary.regularByDay[day] || [])].sort(
                  (a, b) => (a.start || "").localeCompare(b.start || ""),
                );

                return entries.map((entry, index) => (
                  <AssignmentRow
                    key={`${day}-${entry.sessionKey}-${index}`}
                    accent={entry.siteAccent || getSiteAccent(0)}
                    leading={DAY_SHORT_LABELS[day] || day}
                    timeLabel={`${entry.start}–${entry.end}`}
                    title={entry.title || "Training"}
                    footerLabel={
                      entry.rangeLabel
                        ? `${entry.siteName} · ${entry.rangeLabel}`
                        : entry.siteName
                    }
                  />
                ));
              })}
            </Stack>
          )}
        </Box>

        <Divider />

        <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 1.5, pb: 1.5 }}>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Iconify
              icon="mdi:calendar-check-outline"
              width={18}
              sx={{ color: "warning.main" }}
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              Trainings im Monat
            </Typography>
            <Chip
              size="small"
              label={formatTrainingLabel(summary.monthTrainingCount)}
              sx={{
                height: 20,
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                color: "warning.dark",
              }}
            />
          </Stack>

          {summary.monthActivityDays.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              In diesem Monat sind dir keine Trainings zugeordnet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {summary.monthActivityDays.map((day) => (
                <MonthActivityDay
                  key={day.date}
                  day={day}
                  deletingEntryKey={deletingEntryKey}
                  onDeleteEntry={onDeleteEntry}
                  showDeleteActions={showDeleteActions}
                  showDeleteText={showDeleteText}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Paper>

      <Box sx={{ textAlign: "center", py: 0.5 }}>
        <Button
          size="small"
          variant="text"
          startIcon={<Iconify icon="mdi:calendar-month-outline" />}
          onClick={onShowPlan}
        >
          Zum Trainingsplan
        </Button>
      </Box>
    </Stack>
  );
}

function SummaryMetric({ label, value }) {
  return (
    <Box
      sx={{
        flex: 1,
        px: { xs: 1.5, md: 2 },
        py: 1.25,
        textAlign: "center",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", fontSize: "0.68rem" }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );
}

function MonthActivityDay({
  day,
  deletingEntryKey,
  onDeleteEntry,
  showDeleteActions,
  showDeleteText,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2.5,
        borderColor: "divider",
        backgroundColor: "background.default",
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.35,
              }}
            >
              {formatDateLabel(day.date, { weekday: "long" })}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatDateLabel(day.date, {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Chip
              size="small"
              label={formatTrainingLabel(day.count)}
              sx={{
                height: 22,
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: alpha("#0f172a", 0.06),
                color: "text.primary",
              }}
            />
            <Chip
              size="small"
              label={formatDurationLabel(day.totalMinutes)}
              sx={{
                height: 22,
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: alpha("#0f172a", 0.04),
                color: "text.secondary",
              }}
            />
          </Stack>
        </Stack>

        <Stack spacing={0.5}>
          {day.entries.map((entry, index) => (
            <AssignmentRow
              key={
                entry.entryKey ||
                `${day.date}-${
                  entry.sessionKey || entry.title || index
                }-${index}`
              }
              accent={entry.siteAccent || getSiteAccent(0)}
              leading={
                <OccurrenceBadge occurrenceType={entry.occurrenceType} />
              }
              timeLabel={`${entry.start}–${entry.end}`}
              title={entry.title || "Training"}
              footerLabel={`${entry.siteName} · ${
                entry.occurrenceType === "single"
                  ? "Einzeltermin"
                  : "Regelmäßig"
              }`}
              timeMinWidth={74}
              trailingAction={
                showDeleteActions ? (
                  <SummaryDeleteAction
                    entry={entry}
                    isDeleting={deletingEntryKey === entry.entryKey}
                    onDelete={onDeleteEntry}
                    showText={showDeleteText}
                  />
                ) : null
              }
            />
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

function OccurrenceBadge({ occurrenceType }) {
  const isSingle = occurrenceType === "single";

  return (
    <Chip
      size="small"
      icon={
        <Iconify
          icon={isSingle ? "mdi:calendar-star-outline" : "mdi:repeat"}
          width={14}
        />
      }
      label={isSingle ? "Einzel" : "Fix"}
      sx={{
        height: 22,
        minWidth: 62,
        fontSize: "0.66rem",
        fontWeight: 700,
        backgroundColor: isSingle
          ? alpha("#d97706", 0.12)
          : alpha("#0e7490", 0.1),
        color: isSingle ? "#b45309" : "#0e7490",
        "& .MuiChip-icon": {
          color: "inherit",
          ml: 0.4,
          mr: -0.2,
        },
      }}
    />
  );
}

function formatTrainingLabel(count) {
  return `${count} Training${count === 1 ? "" : "s"}`;
}

function SummaryDeleteAction({ entry, isDeleting, onDelete, showText }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isRegular = entry.occurrenceType === "regular";
  const buttonLabel = isDeleting ? "Lösche..." : "Löschen";
  const ariaLabel = isDeleting
    ? "Buchung wird gelöscht"
    : isRegular
    ? `Löschoptionen für ${entry.title || "Training"} öffnen`
    : `${entry.title || "Training"} löschen`;
  const entryDateLabel = formatDateLabel(entry.date, {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const handleOpen = () => {
    if (isDeleting) return;
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    if (isDeleting) return;
    setIsDialogOpen(false);
  };

  const handleDelete = (options = {}) => {
    setIsDialogOpen(false);
    onDelete(entry, options);
  };

  if (showText) {
    return (
      <>
        <Button
          color="error"
          size="small"
          variant="contained"
          startIcon={<Iconify icon="mdi:trash-can-outline" width={16} />}
          onClick={handleOpen}
          disabled={isDeleting}
          sx={{ alignSelf: "center", flexShrink: 0, minWidth: 0, px: 1 }}
        >
          {buttonLabel}
        </Button>
        <TrainerAssignmentDeleteDialog
          trainingTitle={entry.title}
          entryDateLabel={entryDateLabel}
          isDeleting={isDeleting}
          isOpen={isDialogOpen}
          isRegular={isRegular}
          onClose={handleClose}
          onDelete={handleDelete}
        />
      </>
    );
  }

  return (
    <>
      <IconButton
        color="error"
        size="small"
        onClick={handleOpen}
        disabled={isDeleting}
        aria-label={ariaLabel}
        sx={{ alignSelf: "center", flexShrink: 0 }}
      >
        <Iconify icon="mdi:trash-can-outline" width={18} />
      </IconButton>
      <TrainerAssignmentDeleteDialog
        trainingTitle={entry.title}
        entryDateLabel={entryDateLabel}
        isDeleting={isDeleting}
        isOpen={isDialogOpen}
        isRegular={isRegular}
        onClose={handleClose}
        onDelete={handleDelete}
      />
    </>
  );
}

function AssignmentRow({
  accent,
  leading,
  timeLabel,
  title,
  footerLabel,
  timeMinWidth = 80,
  trailingAction = null,
}) {
  const leadingContent =
    typeof leading === "string" ? (
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          minWidth: 22,
          color: "text.secondary",
        }}
      >
        {leading}
      </Typography>
    ) : (
      leading
    );

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={0.75}
      sx={{
        py: 0.5,
        px: 1,
        borderRadius: 1.5,
        borderLeft: `3px solid ${accent.text}`,
        backgroundColor: alpha(accent.text, 0.04),
      }}
    >
      {leadingContent}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          minWidth: timeMinWidth,
          fontSize: "0.8rem",
        }}
      >
        {timeLabel}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            fontSize: "0.8rem",
            lineHeight: 1.35,
          }}
          noWrap
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            fontSize: "0.7rem",
            lineHeight: 1.35,
          }}
          noWrap
        >
          {footerLabel}
        </Typography>
      </Box>
      {trailingAction}
    </Stack>
  );
}
